import { Form, Modal, Upload } from "antd";
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Papa from "papaparse";
import ExcelJS from 'exceljs';
import { bulkUpdateLeaveAllocation, getAllLeaveAllocation } from '../../../redux/rtk/features/leavePolicy/leavePolicySlice';
import { exportToStyledExcel } from '../../../utils/excelExport';

const AllocatedLeaveModal = ({ isOpen, onClose, selectedData, csvData, csvHeaders, csvFileName, selectedValidityId }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        onClose();
    };

    const handleFileChange = (info) => {
        let newFileList = [...info.fileList];
        // Limit to only one file
        newFileList = newFileList.slice(-1);
        setFileList(newFileList);
    };

    const parseExcelFile = async (file) => {
        const workbook = new ExcelJS.Workbook();
        const arrayBuffer = await file.arrayBuffer();
        await workbook.xlsx.load(arrayBuffer);
        
        const worksheet = workbook.worksheets[0];
        const data = [];
        const headers = [];
        
        // Get headers from first row (or second if there's a title)
        let headerRow = worksheet.getRow(1);
        
        // Check if first row is a title (merged cells or single value)
        const firstCell = worksheet.getCell('A1');
        if (firstCell.isMerged || worksheet.rowCount === 1) {
            headerRow = worksheet.getRow(2);
        }
        
        // Extract headers
        headerRow.eachCell((cell, colNumber) => {
            headers[colNumber - 1] = cell.value;
        });
        
        // Extract data rows
        const startRow = headerRow.number + 1;
        for (let i = startRow; i <= worksheet.rowCount; i++) {
            const row = worksheet.getRow(i);
            const rowData = {};
            let hasData = false;
            
            row.eachCell((cell, colNumber) => {
                if (headers[colNumber - 1]) {
                    rowData[headers[colNumber - 1]] = cell.value;
                    hasData = true;
                }
            });
            
            if (hasData) {
                data.push(rowData);
            }
        }
        
        return data;
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            toast.error("Please select a file to upload");
            return;
        }

        if (!selectedValidityId) {
            toast.error("Please select a validity period first");
            return;
        }

        setUploading(true);

        try {
            const file = fileList[0].originFileObj;
            const fileName = file.name.toLowerCase();
            const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
            const isCSV = fileName.endsWith('.csv');
            
            if (!isExcel && !isCSV) {
                toast.error("Please upload a CSV or Excel file");
                setUploading(false);
                return;
            }

            let parsedData = [];

            if (isExcel) {
                // Parse Excel file
                try {
                    parsedData = await parseExcelFile(file);
                    
                    if (!parsedData || parsedData.length === 0) {
                        toast.error("Excel file is empty");
                        setUploading(false);
                        return;
                    }
                } catch (error) {
                    toast.error("Error parsing Excel file");
                    console.error("Excel parse error:", error);
                    setUploading(false);
                    return;
                }
            } else {
                // Parse CSV file
                await new Promise((resolve, reject) => {
                    Papa.parse(file, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                            if (!results.data || results.data.length === 0) {
                                toast.error("CSV file is empty");
                                setUploading(false);
                                reject(new Error("Empty CSV"));
                                return;
                            }
                            parsedData = results.data;
                            resolve();
                        },
                        error: (error) => {
                            toast.error("Error parsing CSV file");
                            console.error("Parse error:", error);
                            setUploading(false);
                            reject(error);
                        }
                    });
                });
            }

            // Process the parsed data
            try {
                // Validate and prepare data
                const allocations = parsedData.map((row) => {
                    return {
                        employeeId: row['Employee ID'] || row.employeeId,
                        employeeName: row['Employee Name'] || row.employeeName,
                        leaveType: row['Leave Type'] || row.leaveType,
                        annualLeave: parseFloat(row['Total Leave'] || row['Annual Leave'] || row.annualLeave || 0),
                    };
                }).filter(item => item.employeeId && item.leaveType);

                if (allocations.length === 0) {
                    toast.error("No valid data found in file");
                    setUploading(false);
                    return;
                }

                // Dispatch bulk update
                await dispatch(bulkUpdateLeaveAllocation({ 
                    allocations, 
                    forceUpdate: false,
                    validityId: selectedValidityId
                })).unwrap();
                
                // Refresh leave allocations to show updated data
                await dispatch(getAllLeaveAllocation()).unwrap();
                
                // Close modal
                handleCancel();
            } catch (error) {
                toast.error("Error processing file data");
                console.error("Processing error:", error);
            } finally {
                setUploading(false);
            }
        } catch (error) {
            toast.error("Error uploading file");
            console.error("Upload error:", error);
            setUploading(false);
        }
    };

    const uploadProps = {
        beforeUpload: (file) => {
            // Check file type (accept CSV, Excel files)
            const isValidType = 
                file.type === 'text/csv' || 
                file.type === 'application/vnd.ms-excel' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            
            if (!isValidType) {
                toast.error('You can only upload CSV or Excel files!');
                return false;
            }

            // Check file size (limit to 5MB)
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                toast.error('File must be smaller than 5MB!');
                return false;
            }

            return false; // Prevent auto upload
        },
        onChange: handleFileChange,
        fileList,
        accept: '.csv,.xlsx,.xls',
    };

    return (
        <Modal
            title="Upload Leave Allocation File"
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
            width={600}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Select File"
                >
                    <Upload.Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for CSV (.csv) or Excel (.xlsx, .xls) files containing leave allocation data
                        </p>
                    </Upload.Dragger>
                </Form.Item>

                <div style={{ marginBottom: '16px' }}>
                    <button
                        type="button"
                        onClick={async () => {
                            if (!csvData || csvData.length === 0) {
                                toast.error('No sample data available');
                                return;
                            }
                            try {
                                await exportToStyledExcel(
                                    csvData,
                                    csvHeaders,
                                    csvFileName?.replace('.csv', '') || 'Leave_Allocation_Upload',
                                    {
                                        headerColor: '28a745',
                                        sheetName: 'Leave Allocation',
                                        title: 'Leave Allocation'
                                    }
                                );
                            } catch (error) {
                                toast.error('Failed to download template');
                                console.error(error);
                            }
                        }}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#28a745',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#28a745';
                            e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'white';
                            e.target.style.color = '#28a745';
                        }}
                    >
                        <DownloadOutlined />
                        Download Sample
                    </button>
                </div>

                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '8px',
                    marginTop: '16px'
                }}>
                    <button 
                        type="button" 
                        onClick={handleCancel}
                        disabled={uploading}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            background: 'white',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            opacity: uploading ? 0.6 : 1
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button"
                        onClick={handleUpload}
                        disabled={fileList.length === 0 || uploading}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            background: fileList.length === 0 || uploading ? '#d9d9d9' : '#1890ff',
                            color: 'white',
                            cursor: fileList.length === 0 || uploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>
            </Form>
        </Modal>
    );
};

export default AllocatedLeaveModal;

